import { Fragment, useMemo, type ReactNode } from 'react';
import { Building2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SearchInput } from '~/components/common/SearchInput';
import { EmptyState } from '~/components/ui/EmptyState';
import { RetryBanner } from '~/components/ui/RetryBanner';
import { useDebouncedSearchInput } from '~/hooks/useDebouncedSearchInput';
import { usePaginatedQuery } from '~/hooks/usePaginatedQuery';
import { useGetTradingPartnerOrganizationsForBuyer } from '~/api/organizations';
import type { GetTradingPartnerOrganizationsForBuyerQuery } from '~/graphql/graphql';
import type { SavedContact } from '~/utils/contact';

export type PickableSellerOrganization =
  GetTradingPartnerOrganizationsForBuyerQuery['tradingPartnerOrganizationsForBuyer']['content'][number];

export type SellerOrganizationPickerLabels = {
  searchPlaceholder: string;
  searchLabel: string;
  loading: string;
  error: string;
  emptyTitle: string;
  emptyMessage: string;
  noResultsTitle: string;
  noResultsMessage: string;
};

export type SellerOrganizationPickerProps = Readonly<{
  labels: SellerOrganizationPickerLabels;
  testId: string;
  excludeOrganizationIds?: string[];
  pinnedOrganizationIds?: string[];
  pageSize?: number;
  listClassName?: string;
  renderOrganization: (
    organization: PickableSellerOrganization,
    listContact: (contact: SavedContact) => void
  ) => ReactNode;
}>;

const DEFAULT_PAGE_SIZE = 20;
const PINNED_PAGE_SIZE = 100;
const NO_ORGANIZATION_IDS: string[] = [];

const selectTradingPartners = (
  data: GetTradingPartnerOrganizationsForBuyerQuery | undefined
) => data?.tradingPartnerOrganizationsForBuyer;

function pinnedFirst(
  organizations: PickableSellerOrganization[],
  pinnedResults: PickableSellerOrganization[],
  pinnedOrganizationIds: string[]
): PickableSellerOrganization[] {
  const pinnedIds = new Set(pinnedOrganizationIds);
  const pinned = pinnedResults.filter(({ id }) => pinnedIds.has(id));
  const shownFirstIds = new Set(pinned.map(({ id }) => id));
  return [
    ...pinned,
    ...organizations.filter(({ id }) => !shownFirstIds.has(id)),
  ];
}

/**
 * Paged, searchable list of the selling organizations the signed-in buyer trades with.
 * A row passes a contact the buyer adds to `listContact` to show it under its organization.
 * `pinnedOrganizationIds` are listed first, wherever they fall in the paged list, except while
 * the buyer searches.
 *
 * @example
 *   <SellerOrganizationPicker
 *     testId="rfq-sellers"
 *     labels={sellerPickerLabels}
 *     renderOrganization={(organization, listContact) => (
 *       <SellerRow organization={organization} onContactAdded={listContact} />
 *     )}
 *   />
 */
export function SellerOrganizationPicker({
  labels,
  testId,
  excludeOrganizationIds = NO_ORGANIZATION_IDS,
  pinnedOrganizationIds = NO_ORGANIZATION_IDS,
  pageSize = DEFAULT_PAGE_SIZE,
  listClassName,
  renderOrganization,
}: SellerOrganizationPickerProps) {
  const { t } = useTranslation('general');
  const { searchInputValue, activeSearch, handleSearchChange } =
    useDebouncedSearchInput();

  const filters = useMemo(
    () => ({ search: activeSearch, excludeOrganizationIds }),
    [activeSearch, excludeOrganizationIds]
  );

  const {
    data: organizations,
    sentinelRef,
    isLoading,
    error,
    refetch,
    updateItem,
  } = usePaginatedQuery({
    fetchHook: useGetTradingPartnerOrganizationsForBuyer,
    select: selectTradingPartners,
    options: { pageSize, filters },
  });

  const isSearching = activeSearch.trim().length > 0;
  const listsPinnedFirst = pinnedOrganizationIds.length > 0 && !isSearching;

  const pinnedFilters = useMemo(
    () => ({
      excludeOrganizationIds,
      onlyOrganizationIds: [...pinnedOrganizationIds].sort((a, b) =>
        a.localeCompare(b)
      ),
    }),
    [excludeOrganizationIds, pinnedOrganizationIds]
  );

  const {
    data: pinnedResults,
    updateItem: updatePinnedItem,
    error: pinnedError,
    refetch: refetchPinned,
  } = usePaginatedQuery({
    fetchHook: useGetTradingPartnerOrganizationsForBuyer,
    select: selectTradingPartners,
    options: {
      pageSize: PINNED_PAGE_SIZE,
      filters: pinnedFilters,
      eager: true,
      enabled: listsPinnedFirst,
    },
  });

  const listedOrganizations = listsPinnedFirst
    ? pinnedFirst(organizations, pinnedResults, pinnedOrganizationIds)
    : organizations;

  const listContact = (organizationId: string, contact: SavedContact) => {
    const addContact = (listed: PickableSellerOrganization) => ({
      ...listed,
      sellerContacts: [...listed.sellerContacts, contact],
    });
    updateItem(organizationId, addContact);
    updatePinnedItem(organizationId, addContact);
  };

  const hasOrganizations = organizations.length > 0;

  const renderBody = () => {
    if (error) {
      return (
        <RetryBanner
          message={labels.error}
          retryLabel={t('general.retry')}
          onRetry={() => void refetch()}
          testId={testId}
        />
      );
    }

    if (isLoading && !hasOrganizations) {
      return (
        <p className="text-sm text-neut-sec" data-testid={`${testId}-loading`}>
          {labels.loading}
        </p>
      );
    }

    if (!hasOrganizations) {
      return (
        <div
          data-testid={isSearching ? `${testId}-no-results` : `${testId}-empty`}
        >
          <EmptyState
            icon={<Building2 size={28} aria-hidden="true" />}
            title={isSearching ? labels.noResultsTitle : labels.emptyTitle}
            message={
              isSearching ? labels.noResultsMessage : labels.emptyMessage
            }
          />
        </div>
      );
    }

    return (
      <>
        {listsPinnedFirst && pinnedError && pinnedResults.length === 0 && (
          <RetryBanner
            message={labels.error}
            retryLabel={t('general.retry')}
            onRetry={() => void refetchPinned()}
            testId={`${testId}-pinned`}
          />
        )}
        <ul
          data-testid={`${testId}-list`}
          className={listClassName ?? 'flex flex-col gap-2'}
        >
          {listedOrganizations.map((organization) => (
            <Fragment key={organization.id}>
              {renderOrganization(organization, (contact) =>
                listContact(organization.id, contact)
              )}
            </Fragment>
          ))}
        </ul>
        <div ref={sentinelRef} data-testid={`${testId}-sentinel`} />
      </>
    );
  };

  return (
    <div className="flex flex-col gap-3" data-testid={testId}>
      <SearchInput
        value={searchInputValue}
        onChange={handleSearchChange}
        placeholder={labels.searchPlaceholder}
        ariaLabel={labels.searchLabel}
        testId={`${testId}-search`}
      />
      {renderBody()}
    </div>
  );
}
